import EngineCleanupBase from '../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import calendarCache from './CalendarCache';
import CalendarFetcher from './CalendarFetcher';
import calendarScheduler from './CalendarScheduler';
import calendarUpdator from './CalendarUpdater';
import CalEventObj from '../Types/CalEventObj';
import { CalEventStates } from '../Types/CalEventStates';
import jxWindowManager from '../../JxControls/JxWindowManager/JxWindowManager.jsx';
import CalendarCard2 from '../UI/CalendarCard2';

const _name = 'CalendarEngine';

// Fallback layout constants — alleen voor de show()-time stacking-offset.
// De statische default-fallback zelf staat in CalEventObj (_defaultPos).
const DRAWER_WIDTH = 210;
const MARGIN = 8;
const CARD_HEIGHT_STEP = 60;

/**
 * Orchestrates CalendarWidget state.
 * Manages cache lifecycle and reminder scheduling. Backend writes are
 * delegated to CalendarUpdater — this engine never talks to
 * calendarEventService/responseHelper directly, it only decides WHEN to
 * notify based on CalendarUpdater's return value (single notify point,
 * no doNotify flag threading).
 * Singleton — one instance shared across the app.
 * NOTE: inline event handlers — if engine grows, move to _handlers pattern (see Development.Patterns).
 */
class CalendarEngine extends EngineCleanupBase {
    _fetcher = new CalendarFetcher();

    bootup = () => {
        this.initialize(_name);

        // Register CalendarCard2 with JxWindowManager — lifecycle managed via _syncWindows
        jxWindowManager.registerComponent('CalendarCard2', CalendarCard2);

        // calendarScheduler/calendarCache are singletons — same cache instance
        // engine reads from. Scheduler notifies 'dataChanged' itself on
        // transitions (same channel as _notify below) — no callback injection.
        // Started once, no restart needed on addOrUpdate/delete.
        calendarScheduler.start();

        // Fallback REST fetch — replaced by SignalR later
        this._fetchEvents();
    };

    /**
     * Called from SignalR OnCalEventsReceived — fills cache, reschedules reminders.
     * Skips null/undefined entries — never constructs an empty CalEventObj
     * (rk='', ems=0) from a missing raw item.
     * @param {object[]} events - raw CalEvent objects van backend
     */
    onCalEventsReceived = (events) => {
        try {
            if (!Array.isArray(events)) return;

            // Wrap raw objects in CalEventObj for type safety and helper methods
            const mapped = events.filter((e) => e != null).map((e) => new CalEventObj(e));

            if (mapped.length === 0) return;

            calendarCache.addOrUpdate(mapped);

            this._notify();
        } catch (error) {
            this.logError('onEventsReceived', error.message);
        }
    };

    /**
     * Called when a single event is added or updated.
     * @param {object} event - raw CalEvent object van backend
     */
    onCalEventUpdated = (event) => {
        try {
            if (!event) return;
            const mapped = new CalEventObj(event);
            calendarCache.addOrUpdate([mapped]);
            this._notify();
        } catch (error) {
            this.logError('onEventUpdated', error.message);
        }
    };

    /**
     * Called when an event is deleted by rk.
     * @param {string} rk
     */
    onCalEventDeleted = (rk) => {
        try {
            if (!rk) return;
            calendarCache.delete(rk);
            this._notify();
        } catch (error) {
            this.logError('onEventDeleted', error.message);
        }
    };

    /**
     * Returns all upcoming events from cache — sorted ascending by ems.
     * @returns {CalEventObj[]}
     */
    getUpcoming = () => {
        return calendarCache.getUpcoming();
    };

    /**
     * Returns all events currently Visible — shown as floating cards.
     * @returns {CalEventObj[]}
     */
    getVisible = () => {
        return calendarCache.getVisible();
    };

    /**
     * Returns all events from cache including overdue.
     * @returns {CalEventObj[]}
     */
    getAll = () => {
        return calendarCache.getAll();
    };

    /**
     * User dismissed an event — sets stateId to Completed via CalendarUpdater.
     * Removes the event from upcoming, drawer and floating cards.
     * Notifies once, only on a confirmed successful persist.
     * @param {CalEventObj} event
     */
    dismiss = async (event) => {
        try {
            if (!event) return;

            const updated = await calendarUpdator.patchCalEvent(event, { stateId: CalEventStates.Completed });
            if (!updated) return;

            this._notify();
        } catch (error) {
            this.logError('dismiss', error.message);
        }
    };

    /**
     * User minimized a floating card — removes it from floating cards but
     * keeps it in the drawer. If the event is Overdue, this is equivalent
     * to dismiss — set Completed. Otherwise sets stateId back to Active via
     * CalendarUpdater — no longer Visible (floating card gone), still in
     * getUpcoming (drawer).
     * Takes the event object directly — caller (CalendarCard2) already has it.
     * @param {CalEventObj} event
     */
    minimize = async (event) => {
        try {
            if (!event) return;

            if (event.isOverDue()) {
                this.dismiss(event);
                return;
            }

            const updated = await calendarUpdator.patchCalEvent(event, { stateId: CalEventStates.Active });
            if (!updated) return;

            this._notify();
        } catch (error) {
            this.logError('minimize', error.message);
        }
    };

    /**
     * User popped out an event from the drawer — persists stateId=Visible via
     * CalendarUpdater so it appears as a floating card.
     * Takes the event object directly — caller (UpcomingDrawerItem) already
     * has it.
     * CalEventObj guarantees a valid event.pos at all times (real position or
     * the default fallback — see CalEventObj constructor). This only refines
     * it when the event sits on the default fallback — offsets against other
     * currently visible fallback-position cards so multiple pop-outs don't
     * stack on top of each other. Fallback pos is local-only, never persisted —
     * only real drag-stop positions go to BE (see CalEventObj.hasRealPos()).
     * Notifies once, only on a confirmed successful persist.
     * @param {CalEventObj} event
     */
    show = async (event) => {
        try {
            if (!event) return;

            const updated = await calendarUpdator.patchCalEvent(event, { stateId: CalEventStates.Visible });
            if (!updated) return;

            if (!event.hasRealPos()) {
                event.pos = this._freeFallbackPos();
            }

            this._notify();
        } catch (error) {
            this.logError('show', error.message);
        }
    };

    /**
     * User edited the event fields, or created a brand new event — persists
     * via CalendarUpdater. Notifies once, only on a confirmed successful persist.
     * @param {CalEventObj} event
     * @param {object} changes - { label?, date?, time?, cat?, desc?, remind? }
     */
    updateEvent = async (event, changes) => {
        try {
            const updated = await calendarUpdator.updateEvent(event, changes);
            if (!updated) return;

            this._notify();
        } catch (error) {
            this.logError('updateEvent', error.message);
        }
    };

    /**
     * Stops the scheduler — called on app/widget teardown.
     * Overrides EngineCleanupBase.clear() — calls super for pubsub cleanup.
     */
    clear() {
        calendarScheduler.stop();
        super.clear();
    }

    // ─── private ──────────────────────────────────────────

    /**
     * Syncs visible CalEvents with JxWindowManager instances.
     * Opens a CalendarCard2 window for each new visible event, closes windows
     * for events that are no longer visible, and — critically — pushes fresh
     * props onto windows that are already open, via updateWindowProps().
     * Without this last step, an already-open CalendarCard2 keeps rendering
     * the event object reference from whenever it was first opened; label/
     * date/time/ems edits saved via updateEvent() mutate that same object in
     * cache, but nothing ever tells JxWindowManager's painter to repaint, so
     * the floating card stays visually stale until it's closed and reopened.
     * @param {CalEventObj[]} visible
     */
    _syncWindows = (visible) => {
        const open = jxWindowManager.getAllWindows().filter((w) => w.name === 'CalendarCard2');

        // Close windows for events no longer visible
        open
            .filter((w) => !visible.find((e) => e.rk === w.props.event?.rk))
            .forEach((w) => jxWindowManager.closeWindow(w.id));

        // Open windows for new visible events, refresh props for existing ones
        visible.forEach((event, index) => {
            const exists = open.find((w) => w.props.event?.rk === event.rk);
            if (!exists) {
                jxWindowManager.openWindow('CalendarCard2', 'global', { event, index });
                return;
            }

            jxWindowManager.updateWindowProps(exists.id, { event, index });
        });
    };

    /**
     * Returns the default fallback position, offset upward for each currently
     * visible card that's already sitting at the default fallback position —
     * avoids stacking multiple pop-outs on top of each other.
     * @returns {{ top: number, left: number }}
     */
    _freeFallbackPos = () => {
        const occupied = this.getVisible().filter((e) => !e.hasRealPos()).length;

        return {
            top: window.innerHeight - 220 - occupied * CARD_HEIGHT_STEP,
            left: window.innerWidth - 168 - DRAWER_WIDTH - MARGIN,
        };
    };

    /**
     * Temporary REST fallback — replaced by SignalR push later.
     * Fetches all events and fills cache.
     */
    _fetchEvents = async () => {
        try {
            const items = await this._fetcher.fetch();
            if (!items) return;
            this.onCalEventsReceived(items);
        } catch (error) {
            this.logError('_fetchEvents', error.message);
        }
    };

    _notify = () => {
        const visible = this.getVisible();

        this._syncWindows(visible);

        ComponentNotifier.notify('dataChanged', _name, {
            upcoming: this.getUpcoming(),
            visible,
        });
    };
}

const calendarEngine = new CalendarEngine();
export default calendarEngine;
